import {
  downloadFile,
  downloadJsonWithEtag,
  DriveApiError,
  findOrCreateFolder,
  listFilesInFolder,
  updateJsonConditionally,
  uploadFile,
} from './googleDriveService';
import {
  getAllWarbands,
  getOrCreateSyncDeviceId,
  getSyncMetadata,
  getSyncOutbox,
  loadWarbandData,
  removeSyncEntity,
  saveWarbandData,
  setSyncEntityMetadata,
  SyncEntityKey,
} from './localStorageService';
import { customCreatureService } from './customCreatureService';
import { customWeaponService } from './customWeaponService';
import { customArmourService } from './customArmourService';
import { customBuffService } from './customBuffService';
import {
  createEmptyManifest,
  decideReconciliation,
  stableContentHash,
  SYNC_MANIFEST_FILE_NAME,
  SyncEntityConflict,
  SyncManifest,
  SyncManifestEntry,
} from './syncManifest';
import { syncService } from './syncService';

interface ManifestDocument {
  fileId: string;
  manifest: SyncManifest;
  etag: string;
}

export interface ManifestSyncResult {
  conflicts: SyncEntityConflict[];
  imageConflicts: import('./warbandMemberImageService').MemberImageConflict[];
  changedLocally: boolean;
}

const legacyEntityKey = (fileName: string): SyncEntityKey | null => {
  const warbandMatch = fileName.match(/^warband-(.+)\.json$/);
  if (warbandMatch) return `warband:${warbandMatch[1]}`;
  if (fileName === 'custom-bestiary.json') return 'custom-bestiary';
  if (fileName === 'custom-weapons.json') return 'custom-weapons';
  if (fileName === 'custom-armour.json') return 'custom-armour';
  if (fileName === 'custom-buffs.json') return 'custom-buffs';
  return null;
};

function customItemKey(collectionKey: SyncEntityKey, id: string): SyncEntityKey | null {
  switch (collectionKey) {
    case 'custom-bestiary': return `custom-creature:${id}`;
    case 'custom-weapons': return `custom-weapon:${id}`;
    case 'custom-armour': return `custom-armour-item:${id}`;
    case 'custom-buffs': return `custom-buff:${id}`;
    default: return null;
  }
}

const normalizeEntityKey = (entityKey: SyncEntityKey): SyncEntityKey => {
  if (!entityKey.startsWith('portrait:')) return entityKey;
  const [, warbandId] = entityKey.split(':');
  return `warband:${warbandId}`;
};

function localEntityKeys(): SyncEntityKey[] {
  return [
    ...getAllWarbands().map((warband): SyncEntityKey => `warband:${warband.id}`),
    ...customCreatureService.getAll().map((item): SyncEntityKey => `custom-creature:${item.id}`),
    ...customWeaponService.getAll().map((item): SyncEntityKey => `custom-weapon:${item.id}`),
    ...customArmourService.getAll().map((item): SyncEntityKey => `custom-armour-item:${item.id}`),
    ...customBuffService.getAll().map((item): SyncEntityKey => `custom-buff:${item.id}`),
  ];
}

function loadLocalEntity(entityKey: SyncEntityKey): unknown | null {
  if (entityKey.startsWith('warband:')) {
    return loadWarbandData(entityKey.slice('warband:'.length));
  }
  if (entityKey.startsWith('custom-creature:')) {
    return customCreatureService.getAll().find((item) => item.id === entityKey.slice('custom-creature:'.length)) ?? null;
  }
  if (entityKey.startsWith('custom-weapon:')) {
    return customWeaponService.getAll().find((item) => item.id === entityKey.slice('custom-weapon:'.length)) ?? null;
  }
  if (entityKey.startsWith('custom-armour-item:')) {
    return customArmourService.getAll().find((item) => item.id === entityKey.slice('custom-armour-item:'.length)) ?? null;
  }
  if (entityKey.startsWith('custom-buff:')) {
    return customBuffService.getAll().find((item) => item.id === entityKey.slice('custom-buff:'.length)) ?? null;
  }
  switch (entityKey) {
    case 'custom-bestiary': return customCreatureService.getAll();
    case 'custom-weapons': return customWeaponService.getAll();
    case 'custom-armour': return customArmourService.getAll();
    case 'custom-buffs': return customBuffService.getAll();
    default: return null;
  }
}

async function applyRemoteEntity(entityKey: SyncEntityKey, content: unknown): Promise<void> {
  if (entityKey.startsWith('warband:')) {
    const id = entityKey.slice('warband:'.length);
    if (content === null) {
      const { deleteWarband } = await import('./localStorageService');
      deleteWarband(id);
    } else {
      saveWarbandData(id, content as import('./localStorageService').StoredWarbandData);
    }
    return;
  }
  if (entityKey.startsWith('custom-creature:')) {
    const id = entityKey.slice('custom-creature:'.length);
    const all = customCreatureService.getAll().filter((item) => item.id !== id);
    customCreatureService.replaceAll(content === null ? all : [...all, content as import('../types').Creature]);
    return;
  }
  if (entityKey.startsWith('custom-weapon:')) {
    const id = entityKey.slice('custom-weapon:'.length);
    const all = customWeaponService.getAll().filter((item) => item.id !== id);
    customWeaponService.replaceAll(content === null ? all : [...all, content as import('../types').Weapon]);
    return;
  }
  if (entityKey.startsWith('custom-armour-item:')) {
    const id = entityKey.slice('custom-armour-item:'.length);
    const all = customArmourService.getAll().filter((item) => item.id !== id);
    customArmourService.replaceAll(content === null ? all : [...all, content as import('../types').Armour]);
    return;
  }
  if (entityKey.startsWith('custom-buff:')) {
    const id = entityKey.slice('custom-buff:'.length);
    const all = customBuffService.getAll().filter((item) => item.id !== id);
    customBuffService.replaceAll(content === null ? all : [...all, content as import('../types').BuffOrDebuff]);
    return;
  }
  switch (entityKey) {
    case 'custom-bestiary': customCreatureService.replaceAll(content as import('../types').Creature[]); break;
    case 'custom-weapons': customWeaponService.replaceAll(content as import('../types').Weapon[]); break;
    case 'custom-armour': customArmourService.replaceAll(content as import('../types').Armour[]); break;
    case 'custom-buffs': customBuffService.replaceAll(content as import('../types').BuffOrDebuff[]); break;
  }
}

async function migrateLegacyManifest(
  accessToken: string,
  folderId: string
): Promise<ManifestDocument> {
  const files = await listFilesInFolder(accessToken, folderId);
  const manifest = createEmptyManifest();
  for (const file of files) {
    const entityKey = legacyEntityKey(file.name);
    if (!entityKey) continue;
    const content = await downloadFile(accessToken, file.id);
    if (Array.isArray(content) && !entityKey.startsWith('warband:')) {
      for (const item of content as Array<{ id?: string }>) {
        if (!item.id) continue;
        const itemKey = customItemKey(entityKey, item.id);
        if (!itemKey) continue;
        const itemFileId = await uploadFile(
          accessToken,
          folderId,
          `sync-${itemKey.replace(/[^a-zA-Z0-9_-]/g, '-')}-r1-legacy.json`,
          item
        );
        manifest.entities[itemKey] = {
          revision: 1,
          contentHash: stableContentHash(item),
          modifiedAt: file.modifiedTime ?? new Date().toISOString(),
          deviceId: 'legacy',
          fileId: itemFileId,
        };
      }
      continue;
    }
    manifest.entities[entityKey] = {
      revision: 1,
      contentHash: stableContentHash(content),
      modifiedAt: file.modifiedTime ?? new Date().toISOString(),
      deviceId: 'legacy',
      fileId: file.id,
    };
  }
  manifest.revision = Object.keys(manifest.entities).length > 0 ? 1 : 0;

  await uploadFile(accessToken, folderId, SYNC_MANIFEST_FILE_NAME, manifest);
  const refreshed = await listFilesInFolder(accessToken, folderId);
  const manifestFile = refreshed
    .filter((file) => file.name === SYNC_MANIFEST_FILE_NAME)
    .sort((left, right) => left.id.localeCompare(right.id))[0];
  if (!manifestFile) throw new Error('Drive manifest was created but could not be found');
  const document = await downloadJsonWithEtag<SyncManifest>(accessToken, manifestFile.id);
  if (!document.etag) throw new Error('Drive did not provide an ETag for the sync manifest');
  return { fileId: manifestFile.id, manifest: document.content, etag: document.etag };
}

async function loadManifest(accessToken: string): Promise<ManifestDocument> {
  const folderId = await findOrCreateFolder(accessToken);
  const files = await listFilesInFolder(accessToken, folderId);
  const manifestFile = files
    .filter((file) => file.name === SYNC_MANIFEST_FILE_NAME)
    .sort((left, right) => left.id.localeCompare(right.id))[0];
  if (!manifestFile) return migrateLegacyManifest(accessToken, folderId);

  const document = await downloadJsonWithEtag<SyncManifest>(accessToken, manifestFile.id);
  if (!document.etag) throw new Error('Drive did not provide an ETag for the sync manifest');
  return { fileId: manifestFile.id, manifest: document.content, etag: document.etag };
}

async function uploadRevision(
  accessToken: string,
  entityKey: SyncEntityKey,
  content: unknown,
  document: ManifestDocument
): Promise<ManifestDocument> {
  const folderId = await findOrCreateFolder(accessToken);
  const remote = document.manifest.entities[entityKey];
  const revision = (remote?.revision ?? 0) + 1;
  const deviceId = getOrCreateSyncDeviceId();
  const safeKey = entityKey.replace(/[^a-zA-Z0-9_-]/g, '-');
  const deleted = content === null;
  const fileId = deleted
    ? remote?.fileId ?? ''
    : await uploadFile(
        accessToken,
        folderId,
        `sync-${safeKey}-r${revision}-${deviceId}.json`,
        content
      );
  const entry: SyncManifestEntry = {
    revision,
    contentHash: stableContentHash(content),
    modifiedAt: new Date().toISOString(),
    deviceId,
    fileId,
    deleted,
  };
  const nextManifest: SyncManifest = {
    ...document.manifest,
    revision: document.manifest.revision + 1,
    entities: { ...document.manifest.entities, [entityKey]: entry },
  };
  await updateJsonConditionally(
    accessToken,
    document.fileId,
    nextManifest,
    document.etag
  );
  const refreshed = await downloadJsonWithEtag<SyncManifest>(accessToken, document.fileId);
  if (!refreshed.etag) throw new Error('Drive did not provide an ETag after manifest update');
  return { fileId: document.fileId, manifest: refreshed.content, etag: refreshed.etag };
}

function recordMetadata(accountId: string, entityKey: SyncEntityKey, entry: SyncManifestEntry): void {
  setSyncEntityMetadata(accountId, entityKey, {
    revision: entry.revision,
    contentHash: entry.contentHash,
    modifiedAt: entry.modifiedAt,
    deviceId: entry.deviceId,
    deleted: entry.deleted,
  });
}

export async function reconcileManifest(
  accessToken: string,
  accountId: string
): Promise<ManifestSyncResult> {
  let document = await loadManifest(accessToken);
  const outbox = getSyncOutbox(accountId);
  const dirtyByKey = new Map(
    outbox.map((entry) => [normalizeEntityKey(entry.entityKey), entry])
  );
  const keys = new Set<SyncEntityKey>([
    ...localEntityKeys(),
    ...Object.keys(document.manifest.entities) as SyncEntityKey[],
    ...Array.from(dirtyByKey.keys()),
  ]);
  const conflicts: SyncEntityConflict[] = [];
  const imageConflicts: ManifestSyncResult['imageConflicts'] = [];
  let changedLocally = false;

  for (const entityKey of Array.from(keys)) {
    const local = loadLocalEntity(entityKey);
    const localHash = local === null ? null : stableContentHash(local);
    const remote = document.manifest.entities[entityKey] ?? null;
    const metadata = getSyncMetadata(accountId)[entityKey];
    const pending = dirtyByKey.get(entityKey);
    const initialDivergence = !metadata && local !== null && remote && localHash !== remote.contentHash;
    const decision = initialDivergence ? 'conflict' : decideReconciliation({
      localExists: local !== null,
      localDirty: Boolean(pending),
      localHash,
      baseRevision: pending?.baseRevision ?? metadata?.revision ?? null,
      remote,
    });

    if (decision === 'conflict' && remote && localHash) {
      conflicts.push({
        entityKey,
        localHash,
        localModifiedAt: pending?.queuedAt ?? metadata?.modifiedAt ?? new Date().toISOString(),
        remote,
      });
      continue;
    }

    if (decision === 'download' && remote) {
      const content = remote.deleted ? null : await downloadFile(accessToken, remote.fileId);
      await applyRemoteEntity(entityKey, content);
      recordMetadata(accountId, entityKey, remote);
      removeSyncEntity(accountId, entityKey);
      changedLocally = true;
      continue;
    }

    if (decision === 'upload') {
      if (local !== null && entityKey.startsWith('warband:')) {
        const warbandId = entityKey.slice('warband:'.length);
        const result = await syncService.syncWarband(warbandId, accessToken);
        if (result.imageConflicts.length > 0) {
          imageConflicts.push(...result.imageConflicts);
          continue;
        }
      }
      const preparedLocal = loadLocalEntity(entityKey);
      try {
        document = await uploadRevision(accessToken, entityKey, preparedLocal, document);
      } catch (error) {
        if (error instanceof DriveApiError && error.status === 412) {
          document = await loadManifest(accessToken);
          const latest = document.manifest.entities[entityKey];
          const expectedRevision = pending?.baseRevision ?? metadata?.revision ?? null;
          if ((latest?.revision ?? null) !== expectedRevision) {
            if (!latest) throw error;
            conflicts.push({
              entityKey,
              localHash: stableContentHash(preparedLocal),
              localModifiedAt: pending?.queuedAt ?? new Date().toISOString(),
              remote: latest,
            });
            continue;
          }
          document = await uploadRevision(accessToken, entityKey, preparedLocal, document);
        } else {
          throw error;
        }
      }
      const published = document.manifest.entities[entityKey];
      if (published) recordMetadata(accountId, entityKey, published);
      removeSyncEntity(accountId, entityKey);
      for (const entry of outbox) {
        if (normalizeEntityKey(entry.entityKey) === entityKey) {
          removeSyncEntity(accountId, entry.entityKey);
        }
      }
      continue;
    }

    if (decision === 'synchronized' && remote) {
      recordMetadata(accountId, entityKey, remote);
      removeSyncEntity(accountId, entityKey);
    } else if (decision === 'synchronized' && !remote) {
      clearEntityOutbox(accountId, entityKey);
    }
  }

  return { conflicts, imageConflicts, changedLocally };
}

function clearEntityOutbox(accountId: string, entityKey: SyncEntityKey): void {
  for (const entry of getSyncOutbox(accountId)) {
    if (normalizeEntityKey(entry.entityKey) === entityKey) {
      removeSyncEntity(accountId, entry.entityKey);
    }
  }
}

export async function resolveManifestConflict(
  accessToken: string,
  accountId: string,
  entityKey: SyncEntityKey,
  choice: 'local' | 'drive'
): Promise<import('./warbandMemberImageService').MemberImageConflict[]> {
  let document = await loadManifest(accessToken);
  const latest = document.manifest.entities[entityKey];

  if (choice === 'drive') {
    if (!latest) throw new Error('The Drive version no longer exists');
    const content = latest.deleted ? null : await downloadFile(accessToken, latest.fileId);
    await applyRemoteEntity(entityKey, content);
    recordMetadata(accountId, entityKey, latest);
    clearEntityOutbox(accountId, entityKey);
    return [];
  }

  if (entityKey.startsWith('warband:')) {
    const result = await syncService.syncWarband(entityKey.slice('warband:'.length), accessToken);
    if (result.imageConflicts.length > 0) return result.imageConflicts;
  }
  const local = loadLocalEntity(entityKey);
  document = await uploadRevision(accessToken, entityKey, local, document);
  const published = document.manifest.entities[entityKey];
  if (published) recordMetadata(accountId, entityKey, published);
  clearEntityOutbox(accountId, entityKey);
  return [];
}

export const manifestSyncService = { reconcileManifest, resolveManifestConflict };